import React, { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { View, Text } from "../base";
import { useTheme } from "../../contexts/ThemeContext";
import { formatMs, TimeRange } from "../lab/labFormat";
import { LlmLabChartMetric, LlmLabTimeseries } from "../../redux/reducers/llmLabSlice";

// A model to draw as one line. `key` is `${provider}|${model}` (matches the
// timeseries payload); `color` is assigned by the page from the current sort.
export type LlmChartModel = {
  key: string;
  provider: string;
  model: string;
  color: string;
};

interface LlmLatencyChartProps {
  timeseries: LlmLabTimeseries;
  models: LlmChartModel[];
  metric: LlmLabChartMetric;
  timeRange: TimeRange;
}

// Model names can contain dots (e.g. "gpt-4.1"), which Recharts' string
// dataKey would treat as a nested path. Draw each line under a synthetic,
// dot-free field ("s0", "s1", …) and keep the real name for the legend/tooltip.
type ChartLine = LlmChartModel & { field: string };

const formatTick = (t: number, timeRange: TimeRange): string => {
  const d = new Date(t * 1000);
  if (timeRange === "24h") return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "numeric", day: "numeric" });
};

const formatTooltipTime = (t: number): string => {
  const d = new Date(t * 1000);
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const makeTooltip =
  (lines: ChartLine[]) =>
  ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const nameByField = new Map(lines.map((l) => [l.field, l.model]));
    return (
      <View className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg">
        <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">{typeof label === "number" ? formatTooltipTime(label) : label}</Text>
        {payload
          .filter((entry: any) => entry.value != null)
          .map((entry: any, index: number) => (
            <View
              key={index}
              className="flex flex-row items-center gap-2"
            >
              <View
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <Text className="text-xs font-medium text-gray-900 dark:text-white">
                {nameByField.get(entry.dataKey) ?? entry.name}: {formatMs(entry.value)}
              </Text>
            </View>
          ))}
      </View>
    );
  };

export const LlmLatencyChart: React.FC<LlmLatencyChartProps> = ({ timeseries, models, metric, timeRange }) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const gridColor = isDark ? "#374151" : "#E5E7EB";
  const textColor = isDark ? "#9CA3AF" : "#6B7280";

  const lines = useMemo<ChartLine[]>(() => models.map((m, i) => ({ ...m, field: `s${i}` })), [models]);

  // Reshape the per-model series into wide rows keyed by timestamp so each
  // model becomes one column/line. Only the models we were handed are plotted.
  const rows = useMemo(() => {
    const seriesByKey = new Map(timeseries.series.map((s) => [`${s.provider}|${s.model}`, s]));
    const byTime = new Map<number, Record<string, number | null>>();
    for (const line of lines) {
      const series = seriesByKey.get(line.key);
      if (!series) continue;
      for (const point of series.points) {
        let row = byTime.get(point.t);
        if (!row) {
          row = { t: point.t };
          byTime.set(point.t, row);
        }
        row[line.field] = point[metric];
      }
    }
    return Array.from(byTime.values()).sort((a, b) => (a.t as number) - (b.t as number));
  }, [timeseries, lines, metric]);

  if (lines.length === 0 || rows.length === 0) {
    return (
      <View className="flex items-center justify-center h-[260px]">
        <Text className="text-sm text-gray-400 dark:text-gray-500">No time-series data for this slice.</Text>
      </View>
    );
  }

  return (
    <ResponsiveContainer
      width="100%"
      height={280}
    >
      <LineChart
        data={rows}
        margin={{ top: 12, right: 16, left: 0, bottom: 5 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={gridColor}
        />
        <XAxis
          dataKey="t"
          type="number"
          domain={["dataMin", "dataMax"]}
          tick={{ fill: textColor, fontSize: 10 }}
          tickLine={{ stroke: gridColor }}
          axisLine={{ stroke: gridColor }}
          tickFormatter={(v: number) => formatTick(v, timeRange)}
          minTickGap={28}
        />
        <YAxis
          tick={{ fill: textColor, fontSize: 10 }}
          tickLine={{ stroke: gridColor }}
          axisLine={{ stroke: gridColor }}
          tickFormatter={(v: number) => formatMs(v)}
          width={56}
        />
        <Tooltip content={makeTooltip(lines)} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {lines.map((line) => (
          <Line
            key={line.field}
            type="monotone"
            dataKey={line.field}
            name={line.model}
            stroke={line.color}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
            connectNulls
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};
